import { type ReactNode } from 'react';
import cx from 'clsx';
import * as styles from './ProductChainPicker.css';
import type { ChainNode, ChainTarget, ProductChainPickerProps } from './types';
import { EMPTY_PATHS, nodeKey } from './navigation';
import { useProductChainNavigation } from './useProductChainNavigation';
export type { ProductChainPickerProps } from './types';

const DEFAULT_SELECTABLE_KINDS: readonly ChainTarget['kind'][] = ['product'];

function nodeKindLabel(kind: ChainNode['kind']): string {
  switch (kind) {
    case 'service-category':
      return 'Service category';
    default:
      return `${kind[0].toUpperCase()}${kind.slice(1)}`;
  }
}

function columnTitle(options: readonly ChainNode[]): string {
  return Array.from(new Set(options.map((node) => nodeKindLabel(node.kind)))).join(' / ');
}

/**
 * Controlled product-chain navigation and selection: discovery columns only.
 *
 * The picker never presents the selected target and never issues queries.
 * Callers present the draft with ChainTargetEntry and own the dialog, form,
 * and adapters.
 */
export function ProductChainPicker({
  value = null,
  path,
  paths = EMPTY_PATHS,
  selectableKinds = DEFAULT_SELECTABLE_KINDS,
  onChange,
  disabled = false,
  className,
}: ProductChainPickerProps): ReactNode {
  const {
    navigation,
    keyboardFocus,
    containerRef,
    columnCount,
    optionsAtDepth,
    selectNode,
    handleOptionFocus,
    handleOptionKeyDown,
  } = useProductChainNavigation({
    value,
    path,
    paths,
    selectableKinds,
    onChange,
    disabled,
  });

  return (
    <div ref={containerRef} className={cx(styles.container, className)} data-testid="product-chain-picker">
      {columnCount > 0 ? (
        <div className={styles.columns} aria-label="Product chain picker columns">
          {Array.from({ length: columnCount }, (_, depth) => {
            const options = optionsAtDepth(depth);
            if (options.length === 0) return null;
            // Roving tabindex: the focused option, else the navigated node,
            // else the first option of the column is the single tab stop.
            const tabStopKey = (() => {
              if (keyboardFocus?.depth === depth && options.some((o) => nodeKey(o) === keyboardFocus.key)) {
                return keyboardFocus.key;
              }
              const navNode = navigation[depth];
              if (navNode && options.some((o) => nodeKey(o) === nodeKey(navNode))) return nodeKey(navNode);
              return options.length > 0 ? nodeKey(options[0]) : null;
            })();
            return (
              <div className={styles.column} key={depth}>
                <div className={styles.columnLabel}>{columnTitle(options)}</div>
                <div className={styles.options} role="listbox" aria-label={columnTitle(options)}>
                  {options.map((node) => {
                    const isSelected = value?.kind === node.kind && value.id === node.id;
                    const isNavigated = navigation[depth] && nodeKey(navigation[depth]) === nodeKey(node);
                    const nodeKeyString = nodeKey(node);
                    const optionIndex = options.findIndex((o) => nodeKey(o) === nodeKeyString);
                    return (
                      <button
                        type="button"
                        className={cx(styles.option, node.kind === 'product' && 'product')}
                        disabled={disabled}
                        key={nodeKeyString}
                        data-testid={`${node.kind}-${node.id}`}
                        data-depth={depth}
                        data-key={nodeKeyString}
                        tabIndex={nodeKeyString === tabStopKey ? 0 : -1}
                        onClick={() => selectNode(node, depth)}
                        onFocus={() => handleOptionFocus(node, depth)}
                        onKeyDown={(event) => handleOptionKeyDown(event, depth, options, optionIndex)}
                        aria-selected={isSelected}
                        data-navigated={isNavigated || undefined}
                        role="option"
                      >
                        {node.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.placeholder} aria-label="Product chain picker placeholder">
          No product chain candidates are available.
        </div>
      )}
    </div>
  );
}
